import {
  createContext,
  useEffect,
  useState,
  ReactNode,
  useContext,
} from "react";

export interface FeatureFlag {
  enabled: boolean;
  description?: string;
}

export interface FeatureFlagFile {
  version?: number;
  lastUpdated?: string;
  flags: Record<string, FeatureFlag>;
}

export type FeatureFlagMap = Record<string, FeatureFlag>;
export const FeatureFlagContext = createContext<FeatureFlagMap>({});

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const [flags, setFlags] = useState<FeatureFlagMap>({});

  useEffect(() => {
    fetch(`/feature-flags.json?cacheBust=${Date.now()}`)
      .then((response) => response.json())
      .then((data: FeatureFlagFile | FeatureFlagMap) => {
        if ("flags" in data) {
          setFlags(data.flags as FeatureFlagMap);
        } else {
          const normalized: FeatureFlagMap = Object.fromEntries(
            Object.entries(data).map(([key, val]) => [
              key,
              {
                enabled: Boolean(val),
              },
            ])
          );
          setFlags(normalized);
        }
      })
      .catch((err) => {
        console.error("Failed to load feature flags", err);
      });
  }, []);

  return (
    <FeatureFlagContext.Provider value={flags}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export const useFeatureFlag = (flagName: string) => {
  const flags = useContext(FeatureFlagContext);
  return flags[flagName];
};
